import * as THREE from 'three';
import { DEG2RAD } from '../utils.js';

/**
 * Configuration for each animated control surface.
 *
 *  mesh   — name of the Object3D in the GLB scene graph
 *  input  — property name on the aircraft ('elevator', 'ailerons', 'rudder', 'flaps')
 *  axis   — local rotation axis: 'x', 'y', or 'z'
 *  gain   — degrees of deflection per unit input (sign sets direction)
 *  min            — minimum deflection in degrees (clamp)
 *  max            — maximum deflection in degrees (clamp)
 *  airbrakeGain   — (optional) degrees added per unit of aircraft.airbrake (for deceleron split)
 */
export const SURFACE_CONFIG = [
  // ── Elevator ────────────────────────────────────────────────────────────────
  { mesh: '_Elevator', input: 'elevator', axis: 'x', gain: -25, min: -25, max: 25 },

  // ── Rudders (twin vertical tails, both deflect the same way) ────────────────
  { mesh: '_Rudder_Left',  input: 'rudder', axis: 'y', gain: -30, min: -25, max: 25 },
  { mesh: '_Rudder_Right', input: 'rudder', axis: 'y', gain: -30, min: -25, max: 25 },

  // ── Decelerons (split ailerons + airbrake) ─────────────────────────────────
  // Aileron: left/right deflect oppositely. Airbrake: tops up, bottoms down (split open).
  { mesh: '_Deceleron_Left_Top',     input: 'ailerons', axis: 'x', gain:  -20, min: -45, max: 45, airbrakeGain: -40 },
  { mesh: '_Deceleron_Left_Bottom',  input: 'ailerons', axis: 'x', gain:  -20, min: -45, max: 45, airbrakeGain:  40 },
  { mesh: '_Deceleron_Right_Top',    input: 'ailerons', axis: 'x', gain: 18,   min: -45, max: 45, airbrakeGain: -40 },
  { mesh: '_Deceleron_Right_Bottom', input: 'ailerons', axis: 'x', gain: 18,   min: -45, max: 45, airbrakeGain:  40 },

  // ── Flaps (no flight-model input yet — wire to 'flaps', defaults to 0) ─────
  { mesh: '_Flap_Left_Inner',  input: 'flaps', axis: 'x', gain: 30, min: 0, max: 30 },
  { mesh: '_Flap_Left_Outer',  input: 'flaps', axis: 'x', gain: 30, min: 0, max: 30 },
  { mesh: '_Flap_Right_Inner', input: 'flaps', axis: 'x', gain: 30, min: 0, max: 30 },
  { mesh: '_Flap_Right_Outer', input: 'flaps', axis: 'x', gain: 30, min: 0, max: 30 },
];

const _euler = new THREE.Euler();
const _targetQuat = new THREE.Quaternion();
const _deflectionQuat = new THREE.Quaternion();

const DEFAULT_SMOOTHING = 5; // per-second lerp speed (higher = snappier)

export default class ControlSurfaceAnimator {
  /**
   * @param {THREE.Object3D} modelRoot — the loaded GLB scene (or its root group)
   * @param {object[]}       config    — array of surface descriptors (defaults to SURFACE_CONFIG)
   * @param {number}         smoothing — lerp speed in units/sec (higher = faster response)
   */
  constructor(modelRoot, config = SURFACE_CONFIG, smoothing = DEFAULT_SMOOTHING) {
    this.surfaces = [];
    this.smoothing = smoothing;

    for (const entry of config) {
      const obj = modelRoot.getObjectByName(entry.mesh);
      if (!obj) {
        console.warn(`ControlSurfaceAnimator: mesh "${entry.mesh}" not found`);
        continue;
      }

      // Preserve the GLB rest-pose quaternion on first access so it survives
      // across scenario reloads (the same model object is reused).
      if (!obj.userData._originalQuat) {
        obj.userData._originalQuat = obj.quaternion.clone();
      }
      const baseQuat = obj.userData._originalQuat.clone();
      obj.quaternion.copy(baseQuat);

      this.surfaces.push({
        obj,
        input: entry.input,
        axis: entry.axis,
        gain: entry.gain,
        airbrakeGain: entry.airbrakeGain || 0,
        min: entry.min * DEG2RAD,
        max: entry.max * DEG2RAD,
        baseQuat,
        currentQuat: baseQuat.clone(),
      });
    }
  }

  /**
   * Call once per frame.
   * @param {Aircraft} aircraft — reads .elevator, .ailerons, .rudder, (.flaps)
   * @param {number}   dt       — frame delta in seconds
   */
  update(aircraft, dt) {
    const t = 1 - Math.exp(-this.smoothing * dt);

    for (const s of this.surfaces) {
      const inputValue = aircraft[s.input] ?? 0;
      const airbrakeValue = s.airbrakeGain ? (aircraft.airbrake ?? 0) : 0;
      const angleDeg = inputValue * s.gain + airbrakeValue * s.airbrakeGain;
      const angleRad = Math.max(s.min, Math.min(s.max, angleDeg * DEG2RAD));

      _euler.set(
        s.axis === 'x' ? angleRad : 0,
        s.axis === 'y' ? angleRad : 0,
        s.axis === 'z' ? angleRad : 0,
      );

      _deflectionQuat.setFromEuler(_euler);
      _targetQuat.copy(s.baseQuat).multiply(_deflectionQuat);
      s.currentQuat.slerp(_targetQuat, t);
      s.obj.quaternion.copy(s.currentQuat);
    }
  }
}
